import { GameConfig } from "@/config/GameConfig";

const STORAGE_KEY = GameConfig.storage.audioPreferencesKey;

interface AudioPreferencesState {
  soundEnabled: boolean;
}

function defaultState(): AudioPreferencesState {
  return { soundEnabled: true };
}

function isValid(value: unknown): value is AudioPreferencesState {
  if (!value || typeof value !== "object") return false;
  return typeof (value as Partial<AudioPreferencesState>).soundEnabled === "boolean";
}

/**
 * The player's persistent Sound On/Off choice - a versioned key entirely
 * separate from progress/Daily/Score storage (see `GameConfig.storage`), so
 * clearing or corrupting one can never touch the other. Malformed or
 * missing data always falls back to sound ON (the default for a new
 * player), never OFF - see PR13-D's production-integration spec section 9.
 */
export const AudioPreferences = {
  get(): AudioPreferencesState {
    try {
      const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
      if (!raw) return defaultState();
      const parsed = JSON.parse(raw);
      return isValid(parsed) ? parsed : defaultState();
    } catch {
      return defaultState();
    }
  },

  save(state: AudioPreferencesState): void {
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage failures - the preference simply won't persist
      // across a reload, which is not worth blocking play over.
    }
  },

  isSoundEnabled(): boolean {
    return this.get().soundEnabled;
  },

  setSoundEnabled(soundEnabled: boolean): void {
    this.save({ soundEnabled });
  },
};
